import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NON_CAT_PART } from '../../../../../../constants/analyticsConstants';
import { REMAN, GENUINE } from '../../../../../../constants/productAssociation';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import ProductLink from '../../../../../common/product/ProductLink';
import useFitmentCheck from '../../../../../../hooks/useFitmentCheck';

const GA_LIST_NAME = 'PCC PDP - Upsell Marketing Associations';

export const ProductUpsell = ({ isPriceCallComplete, uniqueId }) => {
  const { t } = useTranslation();
  const { fireProductImpressionEvent } = useAnalytics();
  const { getFitmentCheck, didFitmentCallResolve } = useFitmentCheck();

  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const merchandisingAssociations = useSelector(
    s => s.products.byId?.[uniqueId]?.merchandisingAssociations
  );
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );

  const getAssociatedPartByType = type =>
    Array.isArray(merchandisingAssociations)
      ? merchandisingAssociations.find(
          ({ associationType }) => associationType === type
        )
      : undefined;

  const remanPart = getAssociatedPartByType(REMAN);
  const genuinePart = getAssociatedPartByType(GENUINE);

  // In case we have Reman and Genuine parts at the same time we'll display genuine
  const upsellProduct = genuinePart || remanPart;
  const { productURL: upsellProductDisplayUrl, partNumber } =
    upsellProduct ?? {};

  const availability = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.availability
  );
  const price = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.discountedPrice
  );
  const unformattedUnitPrice = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.unformattedUnitPrice
  );
  const productWeight = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.weight
  );
  const category = useSelector(
    s => s.products.priceAndAvailability?.[partNumber]?.categoryPath
  );
  const relatedModel = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const showUpsellPrice = !!price && price !== formattedZeroPrice;
  const renderUpsellProduct = !!upsellProductDisplayUrl;
  const brand = upsellProduct?.manufacturer ?? NON_CAT_PART;
  const name = upsellProduct?.name;
  const genuinePartLink = genuinePart && t('CAT_GENUINE');

  const productDetails = useMemo(
    () => ({
      availability,
      brand,
      checkFitment: getFitmentCheck(partNumber),
      name,
      partNumber,
      position: '1',
      price: showUpsellPrice ? unformattedUnitPrice : '',
      category,
      productWeight,
      relatedModel
    }),
    [
      availability,
      brand,
      category,
      getFitmentCheck,
      name,
      partNumber,
      productWeight,
      relatedModel,
      showUpsellPrice,
      unformattedUnitPrice
    ]
  );

  const productClickDetails = {
    ...productDetails,
    list: GA_LIST_NAME
  };

  // Fires a product impression event
  const impressionFired = useRef(false);
  const isReady =
    (isCatCorp || isPriceCallComplete) &&
    (didFitmentCallResolve || !serialNumber);

  useEffect(() => {
    if (!impressionFired.current && renderUpsellProduct && isReady) {
      impressionFired.current = true;
      fireProductImpressionEvent(GA_LIST_NAME, [productDetails]);
    }
  }, [
    fireProductImpressionEvent,
    isReady,
    productDetails,
    renderUpsellProduct
  ]);
  const toShowGenuinePartLink =
    showUpsellPrice && genuinePartLink && isPriceCallComplete;
  return (
    renderUpsellProduct && (
      <div className="col col-lg align-self-lg-center py-2">
        <ProductLink
          className={cx({
            'btn btn-sm btn-ghost text-start p-0': genuinePart
          })}
          link={upsellProductDisplayUrl}
          productClickDetails={productClickDetails}
        >
          {toShowGenuinePartLink
            ? `${genuinePartLink} ${price}`
            : genuinePartLink}
        </ProductLink>
      </div>
    )
  );
};

export default ProductUpsell;

ProductUpsell.propTypes = {
  isPriceCallComplete: PropTypes.bool,
  uniqueId: PropTypes.string
};
